import { useState, useEffect, useCallback, useRef } from 'react';
import { InfoClient, SubscriptionClient } from '../sdk';
import { ConnectionState } from '../sdk/types/transport';
import { ORDER_SIDE_LONG, ORDER_SIDE_SHORT, MARGIN_MODE_CROSS } from '../components/smartlayout/constants';

/**
 * Retry a function with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param initialDelay - Initial delay in ms (default: 1000)
 * @returns Promise that resolves with the function result
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError || new Error("Retry failed");
}

export interface PositionsConfig {
  baseURL: string;
  wsURL?: string; // WebSocket URL (required if useWebSocket is true)
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
  refreshInterval?: number; // in milliseconds (used as fallback when useWebSocket is false or WS is disconnected)
  useWebSocket?: boolean; // Use WebSocket as primary source (default: true)
}

export interface Position {
  marketId: string;
  symbol?: string; // May not be in API response, will need to map from marketId
  size: number; // Decimal value
  entryPrice: number; // Decimal value
  markPrice: number; // Decimal value
  liquidationPrice?: number; // Decimal value
  unrealizedPnL?: number; // Decimal value
  leverage?: number; // Decimal value
  margin?: number; // Calculated or from API
  side?: 'LONG' | 'SHORT'; // Inferred from size sign
  marginMode?: string; // e.g., 'CROSS', 'ISOLATED'
  bucketId?: string | number; // Bucket ID associated with the position
}

/**
 * Hook for managing positions data
 * Uses WebSocket as primary source (via user_updates channel) with HTTP fallback
 * Maintains backward compatibility with polling approach
 */
export function usePositions(config: PositionsConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);
  const isSubscribedRef = useRef<boolean>(false);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Transform API/WS response to Position format
   */
  const transformPosition = useCallback((pos: any): Position => {
    const size = pos.size || 0;
    const side: 'LONG' | 'SHORT' = size >= 0 ? ORDER_SIDE_LONG : ORDER_SIDE_SHORT;
    
    // Calculate margin if not provided (margin = size * entryPrice / leverage)
    let margin = pos.margin;
    if (!margin && pos.leverage && pos.entryPrice && size !== 0) {
      margin = Math.abs(size * pos.entryPrice / pos.leverage);
    }

    return {
      marketId: pos.marketId || pos.market_id || 0,
      symbol: pos.symbol || `m-${pos.marketId || pos.market_id || 'N/A'}`,
      size: Math.abs(size),
      entryPrice: pos.entryPrice || pos.entry_price || 0,
      markPrice: pos.markPrice || pos.mark_price || 0,
      liquidationPrice: pos.liquidationPrice || pos.liquidation_price || undefined,
      unrealizedPnL: pos.unrealizedPnL || pos.unrealized_pnl || pos.pnl || 0,
      leverage: pos.leverage ? Number(pos.leverage) : undefined,
      margin: margin || 0,
      side,
      marginMode: pos.marginMode || pos.margin_mode || MARGIN_MODE_CROSS,
      bucketId: pos.bucketId || pos.bucket_id || undefined,
    };
  }, []);

  /**
   * Fetch positions from API with retry logic
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchPositions = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch positions
    // The API requires an address parameter
    // Don't set error state if address is simply not yet loaded (empty string)
    // Only set error if we've attempted to fetch and it failed
    if (!currentConfig.walletAddress || currentConfig.walletAddress.trim() === '') {
      // Clear error and positions when address is not available
      setError(null);
      setPositions([]);
      setLoading(false);
      return;
    }

    setError(null);

    try {
      const data = await retryWithBackoff(
        () => infoClientRef.current!.getPositionsDecimal(currentConfig.walletAddress!),
        3,
        1000,
      );
      
      if (Array.isArray(data)) {
        const transformedPositions: Position[] = data.map(transformPosition);
        // Compare with current positions to avoid unnecessary state updates
        setPositions(prev => {
          if (prev.length !== transformedPositions.length) {
            return transformedPositions;
          }
          // Deep comparison - only update if positions actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(transformedPositions);
          return prevStr === newStr ? prev : transformedPositions;
        });
      } else {
        setPositions(prev => prev.length === 0 ? prev : []);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load positions';
      setError(errorMessage);
      console.error('Failed to fetch positions after retries:', err);
    } finally {
      setLoading(false);
    }
  }, [transformPosition]); // transformPosition is stable, no config dependencies

  /**
   * Handle position updates from WebSocket (user_updates channel)
   * Backend sends full arrays under data.positions for both initial snapshots and updates
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleUserUpdates = useCallback((data: any) => {
    // Backend sends positions as full arrays: { positions: [...] }
    if (data.positions && Array.isArray(data.positions)) {
      const transformedPositions: Position[] = data.positions.map(transformPosition);
      // Compare with current positions to avoid unnecessary state updates
      setPositions(prev => {
        if (prev.length !== transformedPositions.length) {
          return transformedPositions;
        }
        // Deep comparison - only update if positions actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(transformedPositions);
        return prevStr === newStr ? prev : transformedPositions;
      });
    }
  }, [transformPosition]);

  // Initialize clients and set up WebSocket or polling
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch from HTTP (always do this for fast initial load)
    fetchPositions();

    if (useWebSocket && config.wsURL) {
      // WebSocket mode: primary source
      const subscriptionClient = new SubscriptionClient({
        url: config.wsURL,
        authToken: config.authToken,
        walletAddress: config.walletAddress,
        deviceID: config.deviceID,
      });
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        if (
          subscriptionClient.isConnected() &&
          !isSubscribedRef.current
        ) {
          subscriptionClient.subscribeUserUpdates(handleUserUpdates);
          isSubscribedRef.current = true;
        }
      };

      // Connect WebSocket and subscribe
      subscriptionClient
        .connect()
        .then(() => {
          setIsConnected(true);
          subscribe();
        })
        .catch((err) => {
          console.error('Failed to connect WebSocket:', err);
          setIsConnected(false);
          // Fallback to polling if WS fails
          if (config.refreshInterval && config.refreshInterval > 0) {
            refreshIntervalRef.current = window.setInterval(() => {
              fetchPositions();
            }, config.refreshInterval);
          }
        });

      // Monitor connection state changes and re-subscribe on reconnection
      let unsubscribeStateChange: (() => void) | null = null;
      const wsClient = (subscriptionClient as any).wsClient;
      if (wsClient) {
        unsubscribeStateChange = wsClient.onStateChange(
          (state: ConnectionState) => {
            if (state === ConnectionState.Connected) {
              setIsConnected(true);
              // Re-subscribe if we were previously subscribed
              if (isSubscribedRef.current) {
                subscribe();
              }
              // Stop polling when WS is connected
              if (refreshIntervalRef.current !== null) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
              }
            } else if (
              state === ConnectionState.Disconnected ||
              state === ConnectionState.Reconnecting
            ) {
              setIsConnected(false);
              isSubscribedRef.current = false;
              // Fallback to polling when WS is disconnected
              if (config.refreshInterval && config.refreshInterval > 0) {
                if (refreshIntervalRef.current === null) {
                  refreshIntervalRef.current = window.setInterval(() => {
                    fetchPositions();
                  }, config.refreshInterval);
                }
              }
            }
          },
        );
      }

      return () => {
        // Unsubscribe from state changes
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }

        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }

        if (subscriptionClientRef.current) {
          subscriptionClientRef.current.unsubscribeUserUpdates(handleUserUpdates);
          subscriptionClientRef.current.disconnect();
        }
      };
    } else {
      // Polling mode: fallback or when useWebSocket is false
      if (config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(() => {
          fetchPositions();
        }, config.refreshInterval);
      }

      return () => {
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.baseURL,
    config.wsURL,
    config.authToken,
    config.walletAddress,
    config.deviceID,
    config.refreshInterval,
    useWebSocket,
    // Note: fetchPositions and handleUserUpdates are intentionally NOT in dependencies
    // fetchPositions uses configRef to access latest config without causing dependency loops
    // handleUserUpdates is stable (only depends on transformPosition) and subscription is managed separately
    // The effect will re-run when walletAddress changes (included above), which is the desired behavior
  ]);

  return {
    positions,
    loading,
    error,
    isConnected,
    refresh: fetchPositions,
  };
}
